// Which optional API routes exist on the server we talk to. Some F2 routes were added by other
// workers in parallel; the OpenAPI document tells us (GET /v1/openapi.json), so the UI can hide
// buttons for routes that are not there instead of showing errors. Pure.

export interface Features {
  /** False when the document could not be read: everything is assumed present and 404s are handled. */
  known: boolean;
  /** GET /workspaces/{ws}/handles */
  handles: boolean;
  /** POST /workspaces/{ws}/canvases/{id}/nodes/{nodeId}/run */
  runNode: boolean;
  /** POST /workspaces/{ws}/elements/{id}/fork */
  forkVersion: boolean;
  /** POST /workspaces/{ws}/elements/{id}/generations */
  elementSheets: boolean;
  /** GET /workspaces/{ws}/elements/{id}/takes */
  elementTakes: boolean;
  /** POST /workspaces/{ws}/takes/{id}/accept */
  acceptTake: boolean;
  /** GET /workspaces/{ws}/presets */
  presets: boolean;
}

export const ASSUME_ALL: Features = {
  known: false,
  handles: true,
  runNode: true,
  forkVersion: true,
  elementSheets: true,
  elementTakes: true,
  acceptTake: true,
  presets: true,
};

type Paths = Record<string, Record<string, unknown>>;

const has = (paths: Paths, re: RegExp, method: string): boolean =>
  Object.entries(paths).some(
    ([p, item]) => re.test(p) && !!item && typeof item === "object" && method in item,
  );

/** Reads an OpenAPI document (paths may carry a `/v1` prefix or not). */
export function detectFeatures(doc: unknown): Features {
  const paths = (doc as { paths?: Paths } | null)?.paths;
  if (!paths || typeof paths !== "object") return ASSUME_ALL;
  return {
    known: true,
    handles: has(paths, /\/workspaces\/\{[^}]+\}\/handles$/, "get"),
    runNode: has(paths, /\/canvases\/\{[^}]+\}\/nodes\/\{[^}]+\}\/run$/, "post"),
    forkVersion: has(paths, /\/elements\/\{[^}]+\}\/fork$/, "post"),
    elementSheets: has(paths, /\/elements\/\{[^}]+\}\/generations$/, "post"),
    elementTakes: has(paths, /\/elements\/\{[^}]+\}\/takes$/, "get"),
    acceptTake: has(paths, /\/takes\/\{[^}]+\}\/accept$/, "post"),
    presets: has(paths, /\/workspaces\/\{[^}]+\}\/presets$/, "get"),
  };
}

/** Is a failed call "the server has no such route"? (404 from the router, not "row not found".) */
export function isMissingRoute(e: unknown): boolean {
  const s = (e as { status?: number } | null)?.status;
  return s === 404 || s === 405;
}
